import { BaseQueryFn, FetchArgs, FetchBaseQueryError, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import authService from '../../services/auth/service-auth';
import type { RootState } from '../store';
import { EPartitionCadence, TFilterParams, TFilters } from '../filters/types-filters';
import { ETabId } from '../../services/tab-configurations/tab-types';

// Support empty response from API server, and return undefined
export const handleNonJsonResponseHandler = async (res: Response) => {
  try {
    const json = await res.json();
    return json;
  } catch {
    return undefined;
  }
};

// Custom retry wrapper to bypass retries for HTTP 590/591 and mimic previous maxRetries:2 behavior
const baseFetch = fetchBaseQuery({
  baseUrl: '',
  async prepareHeaders(headers, { getState }) {
    const impersonationAlias = (getState() as RootState).impersonation.impersonatedUser;
    let bearerToken;
    try {
      bearerToken = await authService.getHeaderForScope(authService.MEM_AUTH_SCOPE);
    } catch {
      // If first auth attempt fails, wait to allow fallback to complete
      // The fallback (MSX -> MEM) is triggered in service-auth.ts and needs time to complete
      await new Promise((resolve) => setTimeout(resolve, 200));

      // Retry after allowing fallback to complete
      // This prevents showing error banner to user during MSX->MEM fallback
      try {
        bearerToken = await authService.getHeaderForScope(authService.MEM_AUTH_SCOPE);
      } catch (retryError) {
        bearerToken = '';
        throw retryError;
      }
    }
    headers.append('authorization', bearerToken);
    if ((getState() as RootState).impersonation.impersonatedUser) {
      headers.append('x-impersonation', impersonationAlias.toUpperCase());
    }
    return headers;
  },
});

const ESTIMATION_ENDPOINTS = new Set(['getEstimationRBI', 'getEstimationRBIV2', 'getEstimationUBI']);

const shouldRetry = (error: FetchBaseQueryError, attempt: number, endpointName: string): boolean => {
  // Only short-circuit retries for estimator endpoints on transient 590/591 statuses
  if (ESTIMATION_ENDPOINTS.has(endpointName) && (error?.status === 590 || error?.status === 591)) {
    return false;
  }
  // Allow up to 2 retries after initial attempt (total 3 attempts) for network/5xx (including 590/591 for non-estimator endpoints)
  if (attempt >= 2) return false;
  if (error?.status === 'FETCH_ERROR') return true;
  if (typeof error?.status === 'number' && error.status >= 500) return true;
  return false;
};

const rawSelexpBaseQuery: BaseQueryFn<FetchArgs | string, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
  let attempt = 0;
  while (true) {
    const result: any = await baseFetch(args, api, extraOptions);
    if (!result.error) {
      return result;
    }
    const error: FetchBaseQueryError = result.error;
    const endpointName = api.endpoint;
    if (!shouldRetry(error, attempt, endpointName)) {
      return result;
    }
    attempt += 1;
  }
};

const generateParticipationFilterParams = (filters: TFilters): TFilterParams => {
  const startDate = filters.selectedParticipationPartition?.startDate;
  const endDate = filters.selectedParticipationPartition?.endDate;
  let fiscalMonthId;
  let fiscalQuarterId;
  let fiscalSemesterId;
  switch (filters.selectedParticipationPartitionGroup?.partitionCadence) {
    case EPartitionCadence.Monthly:
      fiscalMonthId = filters.selectedParticipationPartition?.order;
      break;
    case EPartitionCadence.Quarterly:
      fiscalQuarterId = filters.selectedParticipationPartition?.order;
      break;
    case EPartitionCadence.SemiAnnual:
      fiscalSemesterId = filters.selectedParticipationPartition?.order;
      break;
    default:
      break;
  }
  return {
    fiscalYearId: filters.selectedParticipationPartitionGroup?.partitionGroup,
    participationId: filters.participationId,
    personnelNumber: filters.personnelNumber,
    startDate,
    endDate,
    fiscalMonthId,
    fiscalQuarterId,
    fiscalSemesterId,
  };
};

const generatePerformanceFilterParams = (filters: TFilters): TFilterParams => {
  const startDate = filters.selectedPerformancePartition?.startDate;
  const endDate = filters.selectedPerformancePartition?.endDate;
  let fiscalMonthId;
  let fiscalQuarterId;
  let fiscalSemesterId;
  switch (filters.selectedPerformancePartitionGroup?.partitionCadence) {
    case EPartitionCadence.Monthly:
      fiscalMonthId = filters.selectedPerformancePartition?.order;
      break;
    case EPartitionCadence.Quarterly:
      fiscalQuarterId = filters.selectedPerformancePartition?.order;
      break;
    case EPartitionCadence.SemiAnnual:
      fiscalSemesterId = filters.selectedPerformancePartition?.order;
      break;
    default:
      break;
  }
  return {
    fiscalYearId: filters.selectedPerformancePartitionGroup?.partitionGroup,
    participationId: filters.participationId,
    personnelNumber: filters.personnelNumber,
    startDate,
    endDate,
    fiscalMonthId,
    fiscalQuarterId,
    fiscalSemesterId,
  };
};

const generateManagerFilterParams = (filters: TFilters): TFilterParams => {
  const startDate = filters.selectedManagerPartition?.startDate;
  const endDate = filters.selectedManagerPartition?.endDate;
  let fiscalMonthId;
  let fiscalQuarterId;
  let fiscalSemesterId;
  const fiscalYearId = filters.selectedManagerPartitionGroup?.partitionGroup;
  switch (filters.selectedManagerPartitionGroup?.partitionCadence) {
    case EPartitionCadence.Monthly:
      fiscalMonthId = filters.selectedManagerPartition?.order;
      break;
    case EPartitionCadence.Quarterly:
      fiscalQuarterId = filters.selectedManagerPartition?.order;
      break;
    case EPartitionCadence.SemiAnnual:
      fiscalSemesterId = filters.selectedManagerPartition?.order;
      break;
    default:
      break;
  }
  return {
    fiscalYearId,
    participationId: filters.participationId,
    personnelNumber: filters.personnelNumber,
    startDate,
    endDate,
    fiscalMonthId,
    fiscalQuarterId,
    fiscalSemesterId,
  };
};

const generateEarningsFilterParams = (filters: TFilters): TFilterParams => {
  const startDate = filters.selectedPaymentPartition?.earningsStartDate;
  const endDate = filters.selectedPaymentPartition?.earningsEndDate;
  return {
    fiscalYearId: `${filters.selectedPaymentPartition?.fiscalYearId}`,
    participationId: filters.participationId,
    personnelNumber: filters.personnelNumber,
    startDate,
    endDate,
  };
};

export const generateFilterParams = (filters: TFilters): string => {
  let filterParamsObject: TFilterParams;
  switch (filters.activeTab) {
    case ETabId.MyTeamTab:
      filterParamsObject = generateManagerFilterParams(filters);
      break;
    case ETabId.EarningsTab:
      filterParamsObject = generateEarningsFilterParams(filters);
      break;
    case ETabId.PerformanceTab:
      filterParamsObject = generatePerformanceFilterParams(filters);
      break;
    default:
      filterParamsObject = generateParticipationFilterParams(filters);
      break;
  }
  const filterParams = new URLSearchParams();
  // stringifying filter values
  const filterStrings: { [key: string]: string } = {};
  Object.keys(filterParamsObject).forEach((key) => {
    const filterValue = (filterParamsObject as any)[key];
    if (filterValue) {
      filterStrings[key] = String((filterParamsObject as any)[key]);
    }
  });
  if (filterStrings) {
    Object.entries(filterStrings).forEach((item) => filterParams.append(...item));
  }
  return filterParams.toString();
};

const generateCustomParams = (customParams?: { [key: string]: string }): string => {
  if (!customParams) return '';
  const customParamsObject = new URLSearchParams();
  Object.entries(customParams).forEach((item) => customParamsObject.append(...item));
  return `${customParamsObject.toString()}&`;
};

type TCustomFetchArgs = FetchArgs & {
  customParams?: { [key: string]: string };
  noFilters?: boolean; // Send this flag true to prevent filters from being appended on the URL as params.
};

export const selexpApiBaseQuery: BaseQueryFn<string | TCustomFetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
  const { filters } = api.getState() as RootState;
  // gracefully handle scenarios where data to generate the URL is missing
  if (!filters) {
    return {
      error: {
        status: 400,
        statusText: 'Bad Request',
        data: 'Filters are not Ready yet',
      },
    };
  }
  let urlEnd;
  let customParams;
  let noFilters = false;
  if (typeof args === 'string') {
    urlEnd = args;
  } else {
    urlEnd = args.url;
    customParams = args.customParams;
    noFilters = !!args.noFilters;
  }
  // construct a dynamically generated portion of the url
  let adjustedUrl = `${api.endpoint}${urlEnd}`;
  const customParamsString = generateCustomParams(customParams);
  if (customParamsString.length > 0 || !noFilters) {
    adjustedUrl = `${adjustedUrl}?${generateCustomParams(customParams)}${!noFilters ? generateFilterParams(filters) : ''}`;
  }
  const adjustedArgs = typeof args === 'string' ? adjustedUrl : { ...args, url: adjustedUrl };
  // provide the amended url and other params to the raw base query
  return rawSelexpBaseQuery(adjustedArgs, api, extraOptions);
};
